import { yupResolver } from "@hookform/resolvers/yup";
import { Divider } from "@material-ui/core";
import useIsMounted from "ismounted";
import { useContext, useEffect, useMemo } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import styled from "styled-components";
import { StepperContext } from "../../../../components/stepper/context";
import { ProductionBlockchains } from "../../../../store/chain/models/internal";
import { colours } from "../../../../styles/colours";
import { Container } from "../common/components";
import { FormType } from "../common/form";
import {
  FormType as InnerFormType,
  fromArrayLike,
  toArrayLike,
  useFormSchema,
} from "./common";
import { useSelectedChains } from "./hooks";
import { SingleChain } from "./single-chain";

const ChainToggle = styled.div<{ selected: boolean }>`
  padding: 0.2em 0.5em;
  display: inline-block;
  margin: 0.5em;
  border: 1px solid ${colours.blueGrey};
  border-radius: 5px;
  cursor: pointer;

  &:hover {
    background: ${colours.blueGrey};
    color: ${colours.white};
  }
  ${(props) =>
    props.selected
      ? `border-color: ${colours.darkBlueGrey}; background: ${colours.darkBlueGrey}; color: ${colours.white}`
      : ""}
`;

const StyledDivider = styled.div`
  margin: 2em 1em;
`;

export const CryptoChains: React.FunctionComponent<{}> = () => {
  const { setValidation } = useContext(StepperContext);

  const isMounted = useIsMounted();
  const parentForm = useFormContext<FormType>();
  const innerForm = useForm<InnerFormType>({
    resolver: yupResolver(useFormSchema()),
    defaultValues: useMemo(
      () => toArrayLike(parentForm.getValues("configuration.cryptoChains")),
      [parentForm]
    ),
  });

  const [selectedChains, setSelectedChains] = useSelectedChains(innerForm);

  useEffect(() => {
    setValidation(() => () =>
      new Promise<void>((res, rej) => {
        innerForm.handleSubmit((data) => {
          parentForm.setValue(
            "configuration.cryptoChains",
            fromArrayLike(data)
          );
          res();
        })();
      })
    );

    return () => {
      if (isMounted.current) {
        // We have to check if it's mounted otherwise it will clear current validation
        // This is because the prior page's clear validation is run after the new page's setValidation
        setValidation(() => () => Promise.resolve());
      }
    };
  }, [
    setValidation,
    innerForm.handleSubmit,
    parentForm.setValue,
    isMounted.current,
  ]);

  return (
    <FormProvider {...innerForm}>
      <Container>
        {ProductionBlockchains.keys.map((chain) => (
          <ChainToggle
            key={chain}
            onClick={() =>
              setSelectedChains((x) =>
                !x.includes(chain)
                  ? [...x, chain]
                  : x.filter((y) => y !== chain)
              )
            }
            selected={selectedChains.includes(chain)}
          >
            {chain}
          </ChainToggle>
        ))}

        {selectedChains.length > 0 ? (
          <StyledDivider>
            <Divider />
          </StyledDivider>
        ) : null}

        {selectedChains.map((chain) => (
          <SingleChain chain={chain} key={chain} />
        ))}
      </Container>
    </FormProvider>
  );
};
